import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  getSelectedZusatzoptionen,
  GlobalActions,
  GlobalPut, VorversicherungsItemVersicherteGefahr,
  VwgCheckoutProduktPayload
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  AUSLANDS_RISIKOORT,
  Auslandsrisikoort,
  isAuslandsrisikoort,
  Produktlinie
} from '@ps20-frontend/frontend-models';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import { Counter, Feature, PrometheusCounterIncreasePayload, VorschadenArt } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { isExtremwetterUWProcessNecessary, selectFeatureToggle } from '@ps20-frontend/selector';
import { TaasTarifrisikoschluesselVWGV2 } from '@ps20/bff-models/dist/lib/ps20-bff-enums';
import { VwgActions } from '../vwgActions';
import { IAppState } from '../appState';
import { ZusatzoptionenNameVWGEnum } from '../models/zusatzoptionenVWG.model';
import {
  isGefahrSelected,
  isUwProzessForNebengebaeudeNecessary,
  isZusatzoptionSelected,
  mapStateToPremiumAndExtremwetterDisabledSelectorTo
} from '../../app/shared/selectors/produkt.selector';
import { selectExtremwetterschutzActiveValue } from '../../app/shared/selectors/elementar-selbstbehalt.selector';

export function* watchInDashboardUebernehmenSaga() {
  yield takeEvery(VwgActions.ABSPRUNG_INS_DASHBOARD, safeSaga(generateDashboardData, App.VWG_V2));
  yield takeEvery(VwgActions.ABSPRUNG_INS_DASHBOARD, safeSaga(generateProduktData, App.VWG_V2));
}

export function* generateProduktData() {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();

  const elementarZusatzoption = state.produkt.produktlinien
    .find(linie => linie.selected)
    .zusatzoptionen.find(zusatzoption => zusatzoption.name === VorschadenArt.ELEMENTAR);
  const countersIncreases: Array<PrometheusCounterIncreasePayload> = [];
  if (elementarZusatzoption && !elementarZusatzoption.disabled) {
    countersIncreases.push({
      counter: Counter.VWG_ELEMENTAR_VORBELEGT,
      increment: 1
    });
    if (!elementarZusatzoption.isSelected) {
      countersIncreases.push({
        counter: Counter.VWG_ELEMENTAR_ABGEWAEHLT,
        increment: 1
      });
    }
  }

  const elementarZusatzoptionSelected = Boolean(elementarZusatzoption?.isSelected);

  const isExtremwetterUWProcessNecessaryValue = isExtremwetterUWProcessNecessary(
    mapStateToPremiumAndExtremwetterDisabledSelectorTo(state)
  );

  const showEleUpload =
    elementarZusatzoptionSelected && isExtremwetterUWProcessNecessaryValue;

  const vwgCheckoutProdukt = {
    appId: App.VWG_V2,
    angebotsId: state.angebotsId,
    laufzeit: state.vertragsoptionen.vertragslaufzeit,
    uebersichtTitle: 'Wohngebäudeversicherung',
    versichertesRisiko: `${state.risikoort.strasse} ${state.risikoort.hausnummer}, ${state.risikoort.plz} ${state.risikoort.ort}`,
    geokoordinatenDetails: state.risikoort.noneValidated
      ? `Längengrad: ${state.risikoort.longtitude}, Breitengrad: ${state.risikoort.latitude}`
      : undefined,
    vorvertragsnummer: state.vertragsoptionen.vorvertragsNummern,
    vorvertragSofortschutz: state.vertragsoptionen.vorvertragSofortschutz,
    countersIncreases,
    extremwetterschutzBeratunghinweis: shouldExtremwetterschutzBeratungBeDisplayed(state),
    premiumAndExtremwetterDisabled: isExtremwetterUWProcessNecessaryValue,
    beginndatum: state.vertragsoptionen.beginnDatum,
    isImmobilieImAusland: state.risikoort.isImmobilieImAusland,
    angebotsdatum: state.vertragsoptionen.angebotsdatum,
    hasVorversicherung: state.hasVorversicherung,
    showEleUpload,
  } as VwgCheckoutProduktPayload;

  const gefahrenFromErsatz = getGefahrenVorversicherung(state);
  if (gefahrenFromErsatz?.length > 0) {
    vwgCheckoutProdukt.gefahrenVorversicherung = gefahrenFromErsatz;
  }
  vwgCheckoutProdukt.extremwetterschutzSelbstbeteiligung =
    selectExtremwetterschutzActiveValue(state);
  vwgCheckoutProdukt.zuersZone = state.gisDaten?.zuersGefaehrdungsklasse;
  yield call(globalPut.put, new GlobalActions().produktAction(vwgCheckoutProdukt));
}

export function getGefahrenVorversicherung(state: IAppState): Array<VorversicherungsItemVersicherteGefahr> {
  const array = new Array<VorversicherungsItemVersicherteGefahr>();
  if (
    isGefahrSelected(state, TaasTarifrisikoschluesselVWGV2.FEUER) &&
    state.vorschaeden.feuerVorschaden.visibleInErsatzgeschaeft
  ) {
    array.push({
      label: 'Feuer',
      value: VorschadenArt.FEUER,
      selected: false,
    } as VorversicherungsItemVersicherteGefahr);
  }
  if (
    isGefahrSelected(state, TaasTarifrisikoschluesselVWGV2.STURM) &&
    state.vorschaeden.sturmhagelVorschaden.visibleInErsatzgeschaeft
  ) {
    array.push({
      label: 'Sturm/Hagel',
      value: VorschadenArt.STURM_HAGEL,
      selected: false,
    } as VorversicherungsItemVersicherteGefahr);
  }
  if (
    isGefahrSelected(state, TaasTarifrisikoschluesselVWGV2.LEITUNGSWASSER) &&
    state.vorschaeden.leitungswasserVorschaden.visibleInErsatzgeschaeft
  ) {
    array.push({
      label: 'Leitungswasser',
      value: VorschadenArt.LEITUNGSWASSER,
      selected: false,
    } as VorversicherungsItemVersicherteGefahr);
  }
  if (
    isZusatzoptionSelected(state, TaasTarifrisikoschluesselVWGV2.ELEMENTAR) &&
    state.vorschaeden.elementarVorschaden.visibleInErsatzgeschaeft
  ) {
    array.push({
      label: 'Weitere Naturgefahren',
      value: VorschadenArt.ELEMENTAR,
      selected: false,
    } as VorversicherungsItemVersicherteGefahr);
  }
  return array;
}

export function shouldExtremwetterschutzBeratungBeDisplayed(state: IAppState): boolean {
  const produkt = state.produkt.produktlinien[0].zusatzoptionen.find(
    zusatzoption => zusatzoption.name === ZusatzoptionenNameVWGEnum.ELEMENTAR
  );
  return Boolean(produkt) && !produkt.disabled && !produkt.isSelected;
}

export function* generateDashboardData() {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const selectedProduktlinie: Produktlinie = getSelectedProduktlinie(state.produkt);

  yield put(new VwgActions().vorschlagdatenChanged(state.angebotsId));

  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().inDashboardUebernehmen({
      appId: App.VWG_V2,
      angebotsId: state.angebotsId,
      kurztext: selectedProduktlinie.kurztext,
      ausgewaehlteLinie: selectedProduktlinie.produktName,
      versichertesRisiko: getVersichertesRisikoString(state.risikoort, state.gebaeude.wohnflaeche),
      versicherterRisikoort: state.risikoort,
      vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
      selectZusatzoptionen: getSelectedZusatzoptionen(selectedProduktlinie.zusatzoptionen),
      beginn: state.vertragsoptionen.beginnDatum,
      hasVorschaeden: state.vorschaeden.vorhanden,
      nebengebaeudeQM: state.gebaeude?.nebengebaeude?.totalArea,
      nebengebaeudeUeber200: Boolean(
        selectFeatureToggle(state, Feature.UW_NEBENGEBAEUDE) &&
          isUwProzessForNebengebaeudeNecessary(state, state.vertragsoptionen),
      ),
      extremwetterRisk:
        selectedProduktlinie.zusatzoptionen.some(
          zusatzoption => zusatzoption.id === 'weitereNaturgefahren' && zusatzoption.isSelected,
        ) &&
        isExtremwetterUWProcessNecessary(mapStateToPremiumAndExtremwetterDisabledSelectorTo(state)),
    }),
  );
}

export function getVersichertesRisikoString(risikoort: Auslandsrisikoort, wohnflaeche: number) {
  if (isAuslandsrisikoort(risikoort)) {
    return AUSLANDS_RISIKOORT;
  } else {
    const risikoortString = `${risikoort.strasse} ${risikoort.hausnummer}, ${risikoort.plz} ${risikoort.ort}, `;
    const wohnflaecheString = `${wohnflaeche} m²`;
    return `${risikoortString}${wohnflaecheString}`;
  }
}
